/**
 * CSV projection of the unified school print packet — the dialog's
 * "Download as CSV" beside Print. One file per document kind in the job
 * (production records, serving sheets, orders), each a flat table with its
 * own header, because the three kinds share no columns and a spreadsheet
 * wants one header per sheet.
 *
 * The records and serving rows are derived from the SAME job the paper
 * prints — the record rows come from `printBandRows(buildUnifiedItemRows(doc))`,
 * the exact rows the USDA form prints — so a figure on the spreadsheet is a
 * figure on the paper and vice versa. Prep-copy (unsigned) records export
 * their item list with EMPTY counts, exactly as the blank form prints
 * write-in boxes: a count nobody recorded is never exported as a number.
 *
 * The Orders file is different: it is NOT the paper Orders sheet (a per-item
 * tally) but the Customers "Per-student meal orders" report — one row per
 * student meal line, the file schools import downstream — fetched from
 * `GET /api/school-orders/student-orders` and written here byte-for-byte in
 * that report's layout (snake_case header, LF line endings).
 */

import type { ProductionRecordDocument } from '@/api/production-records'
import type { StudentOrderRowWire } from '@/api/school-orders'
import { exportStatusLabel } from '@/components/production/helpers'
import {
  buildUnifiedItemRows,
  type MatrixCheckpoint,
  type MatrixTempCell,
  printBandRows,
} from '@/components/production/record-insights'
import type { ServePrintDay } from '@/lib/cafeteria/print-plan'
import { type Mealtime, MEALTIME_LABELS } from '@/lib/mealtime'
import { type SchoolPrintJob, type SchoolPrintJobDay, sortedDayRecords } from '@/lib/school-print/print-plan'

export interface SchoolPrintCsvFile {
  filename: string
  csv: string
}

export function csvEscape(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
}

function csvLine(cells: readonly (string | number | null | undefined)[]): string {
  return cells.map((cell) => csvEscape(cell == null ? '' : String(cell))).join(',')
}

function csvText(
  header: readonly string[],
  rows: readonly (readonly (string | number | null | undefined)[])[],
  eol: '\r\n' | '\n' = '\r\n',
): string {
  return [csvLine(header), ...rows.map(csvLine)].join(eol) + eol
}

function mealtimeLabel(mealtime: string): string {
  return MEALTIME_LABELS[mealtime as Mealtime] ?? mealtime
}

/** `student-orders-2026-09-14.csv` / `student-orders-2026-09-14-to-2026-09-18.csv`; `days` ascending. */
export function schoolPrintCsvFilename(section: string, days: readonly (SchoolPrintJobDay | string)[]): string {
  const iso = (day: SchoolPrintJobDay | string): string => (typeof day === 'string' ? day : day.iso)
  const first = days[0] ? iso(days[0]) : 'empty'
  const last = days[days.length - 1] ? iso(days[days.length - 1]!) : first
  return first === last ? `${section}-${first}.csv` : `${section}-${first}-to-${last}.csv`
}

// ---------------------------------------------------------------------------
// Production records
// ---------------------------------------------------------------------------

const RECORD_CHECKPOINTS: readonly MatrixCheckpoint[] = ['FACILITY', 'DELIVERY', 'SERVICE']

const RECORDS_HEADER = [
  'Date',
  'Mealtime',
  'Kitchen',
  'Status',
  'Row',
  'Item',
  'Kind',
  'Age band',
  'Serving size',
  'Components',
  'Ordered',
  'Prepared',
  'Received',
  'Served',
  'Reimbursable',
  'Non-reimbursable',
  'Leftover',
  'Unservable',
  'Kitchen °F',
  'Received °F',
  'Serving °F',
] as const

const WRITE_IN_ROW_LABEL: Record<string, string> = {
  LEFTOVER: 'Write-in: re-served leftover',
  BACKUP: 'Write-in: backup meal',
  DROPPED: 'Unservable',
}

/** "47.5 → 39.0" for a re-checked reading, as the form prints it. */
function tempCellText(cell: MatrixTempCell | undefined): string {
  return cell ? cell.readings.map((reading) => reading.value).join(' → ') : ''
}

export function buildProductionRecordsCsv(
  job: SchoolPrintJob,
  documentsById: ReadonlyMap<string, ProductionRecordDocument>,
): SchoolPrintCsvFile | null {
  const rows: (string | number | null)[][] = []
  const daysWithRecords: SchoolPrintJobDay[] = []
  for (const day of job.days) {
    let dayHasRow = false
    for (const record of sortedDayRecords(day.records)) {
      const doc = documentsById.get(record.id)
      if (!doc) {
        continue
      }
      dayHasRow = true
      const base = [day.iso, mealtimeLabel(doc.mealtime), record.vendorName, exportStatusLabel(doc.status)]
      const matrix = buildUnifiedItemRows(doc)
      for (const printRow of printBandRows(matrix.rows)) {
        const { row } = printRow
        const components = printRow.firstOfItem
          ? row.components.map((c) => (c.crediting ? `${c.name} (${c.crediting})` : c.name)).join('; ')
          : ''
        // A prep copy carries no recorded figures (the same D18 rule the
        // paper follows): item, band and serving size only.
        const counts = record.blank
          ? [null, null, null, null, null, null, null, null, '', '', '']
          : [
              printRow.ordered,
              printRow.prepared,
              printRow.delivered,
              printRow.served,
              printRow.reimbursable,
              printRow.nonReimbursable,
              printRow.leftover,
              null,
              ...RECORD_CHECKPOINTS.map((checkpoint) => tempCellText(row.temps[checkpoint])),
            ]
        rows.push([
          ...base,
          'Item',
          row.name,
          row.kindLabel ?? '',
          printRow.band,
          printRow.servingLabel ?? '',
          components,
          ...counts,
        ])
      }
      if (!record.blank) {
        for (const writeIn of doc.writeIns) {
          const dropped = writeIn.kind === 'DROPPED'
          rows.push([
            ...base,
            WRITE_IN_ROW_LABEL[writeIn.kind] ?? writeIn.kind,
            writeIn.name,
            '',
            '',
            '',
            writeIn.description ?? '',
            null,
            null,
            null,
            dropped ? null : writeIn.quantity,
            dropped ? null : writeIn.reimbursable,
            dropped ? null : writeIn.nonReimbursable,
            null,
            dropped ? writeIn.quantity : null,
            '',
            '',
            '',
          ])
        }
      }
    }
    if (dayHasRow) {
      daysWithRecords.push(day)
    }
  }
  if (rows.length === 0) {
    return null
  }
  return { filename: schoolPrintCsvFilename('production-records', daysWithRecords), csv: csvText(RECORDS_HEADER, rows) }
}

// ---------------------------------------------------------------------------
// Serving sheets
// ---------------------------------------------------------------------------

const SERVING_HEADER = [
  'Date',
  'Mealtime',
  'Sheet',
  'Group',
  'Student',
  'Meal',
  'Serving size',
  'Utensils',
  'Planned',
  'Served',
] as const

function servingRowsForDay(day: ServePrintDay): (string | number | null)[][] {
  const rows: (string | number | null)[][] = []
  for (const mealtime of day.mealtimes) {
    const base = [day.serviceDate, mealtime.label]
    // Same visibility rule as the printed section: a student-cards mealtime
    // prints its roster, a quantity-table one its counts, MIXED both.
    const showStudents = mealtime.serveMode === 'STUDENT_CARDS' || mealtime.serveMode === 'MIXED'
    const showQuantity = mealtime.serveMode === 'QUANTITY_TABLE' || mealtime.serveMode === 'MIXED'
    if (showStudents) {
      for (const group of mealtime.rosterGroups) {
        for (const student of group.students) {
          rows.push([
            ...base,
            'Roster',
            group.label ?? student.bucket ?? '',
            student.studentName,
            student.mealLines.join('; '),
            '',
            '',
            null,
            student.served ? 'Yes' : 'No',
          ])
        }
      }
    }
    if (showQuantity) {
      for (const group of mealtime.tallyGroups) {
        for (const row of group.rows) {
          const caption = (label: 'Serving size' | 'Utensils') =>
            row.captionParts.find((part) => part.label === label)?.value ?? ''
          rows.push([
            ...base,
            'Count',
            group.label,
            '',
            row.label,
            caption('Serving size'),
            caption('Utensils'),
            row.planned,
            '',
          ])
        }
      }
    }
  }
  return rows
}

export function buildServingSheetsCsv(job: SchoolPrintJob): SchoolPrintCsvFile | null {
  const rows: (string | number | null)[][] = []
  const daysWithRows: SchoolPrintJobDay[] = []
  for (const day of job.days) {
    if (!day.serveDay) {
      continue
    }
    const dayRows = servingRowsForDay(day.serveDay)
    if (dayRows.length > 0) {
      rows.push(...dayRows)
      daysWithRows.push(day)
    }
  }
  if (rows.length === 0) {
    return null
  }
  return { filename: schoolPrintCsvFilename('serving-sheets', daysWithRows), csv: csvText(SERVING_HEADER, rows) }
}

// ---------------------------------------------------------------------------
// Orders — the per-student meal orders report
// ---------------------------------------------------------------------------

/**
 * The Customers "Per-student meal orders" report header, verbatim — the
 * column the school reconciles against their SIS sits next to the student
 * it identifies. Changing a name or position here re-points every school's
 * downstream import.
 */
export const STUDENT_ORDERS_CSV_HEADER = [
  'meal_name',
  'student_name',
  'sis_student_id',
  'grade',
  'quantity',
  'service_date',
  'mealtime',
  'campus',
] as const

/**
 * One row per student meal line, in the server's (report) order, narrowed
 * to the dialog's selected days — the server answers the whole span, the
 * day pills may have excluded some of it. LF line endings, like the report
 * file. Null when no row survives.
 */
export function buildStudentOrdersCsv(
  rows: readonly StudentOrderRowWire[],
  selectedDays: readonly string[],
): SchoolPrintCsvFile | null {
  const wanted = new Set(selectedDays)
  const kept = rows.filter((row) => wanted.has(row.serviceDate))
  if (kept.length === 0) {
    return null
  }
  const days = [...new Set(kept.map((row) => row.serviceDate))].sort()
  const body = kept.map((row) => [
    row.mealName,
    row.studentName,
    row.sisStudentId,
    row.grade,
    row.quantity,
    row.serviceDate,
    row.mealtime,
    row.campus,
  ])
  return {
    filename: schoolPrintCsvFilename('student-orders', days),
    csv: csvText(STUDENT_ORDERS_CSV_HEADER, body, '\n'),
  }
}

// ---------------------------------------------------------------------------
// Packet
// ---------------------------------------------------------------------------

export interface SchoolPrintCsvInputs {
  /** The assembled packet; null when nothing in it is printable. */
  job: SchoolPrintJob | null
  documentsById: ReadonlyMap<string, ProductionRecordDocument>
  /** Per-student order rows for the span; null when the Orders section was not asked for (or failed). */
  studentOrders: readonly StudentOrderRowWire[] | null
  /** The request's selected days, ascending. */
  selectedDays: readonly string[]
}

/** Every file the packet yields, in packet order (orders, records, serving); empty when nothing is exportable. */
export function buildSchoolPrintCsvFiles(inputs: SchoolPrintCsvInputs): SchoolPrintCsvFile[] {
  const { job, documentsById, studentOrders, selectedDays } = inputs
  return [
    studentOrders ? buildStudentOrdersCsv(studentOrders, selectedDays) : null,
    job ? buildProductionRecordsCsv(job, documentsById) : null,
    job ? buildServingSheetsCsv(job) : null,
  ].filter((file): file is SchoolPrintCsvFile => file !== null)
}

/**
 * Save each file through a transient anchor (the app's CSV idiom —
 * `lib/chefs/csv.ts`). The object URLs are revoked on a delay so a slow
 * browser has finished reading them.
 */
export function downloadSchoolPrintCsvFiles(files: readonly SchoolPrintCsvFile[]): void {
  for (const file of files) {
    const blob = new Blob([file.csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = file.filename
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  }
}
